import React, { Suspense, type ReactNode } from 'react';

import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import type { hubBrandPrimaryColorQuery } from './__generated__/hubBrandPrimaryColorQuery.graphql';

type RenderWithColor = (brandPrimaryColor: string | undefined) => ReactNode;

interface Props {
	children: RenderWithColor;
	cloudId: string;
	helpCenterAri: string;
}

const HubBrandPrimaryColorQueryRenderer = ({ children, cloudId, helpCenterAri }: Props) => {
	// Same field and arguments as the hub bootstrap query (`isDraft: false` included), so Relay
	// normally answers this from the store. Its own query because the bootstrap's copy is masked
	// inside company-hub-common's fragment.
	const data = useLazyLoadQuery<hubBrandPrimaryColorQuery>(
		graphql`
			query hubBrandPrimaryColorQuery($cloudId: ID!, $helpCenterAri: ID!) {
				helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
					helpCenterById(helpCenterAri: $helpCenterAri, isDraft: false)
						@optIn(to: "HelpCenterAggBeta") {
						... on HelpCenter {
							helpCenterBranding @optIn(to: ["HelpCenterBrandingTest"]) {
								colors {
									primary
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId, helpCenterAri },
		{ fetchPolicy: 'store-or-network' },
	);

	const primary = data.helpCenter?.helpCenterById?.helpCenterBranding?.colors?.primary;
	return <>{children(primary || undefined)}</>;
};

/**
 * Resolves the hub primary colour before rendering the widget, because the widget script locks in
 * the first URL it loads with. Never blocks the widget for good: gate off skips the query, and a
 * failed query renders it unbranded, exactly as today.
 */
export const WithHubBrandPrimaryColor = ({ children, cloudId, helpCenterAri }: Props) => {
	di(ErrorBoundary);

	if (!FeatureGates.checkGate('hub_rovo_brand_theming')) {
		return <>{children(undefined)}</>;
	}

	return (
		<ErrorBoundary
			packageName="help-center"
			id="rovo-widget-brand-primary-color"
			renderError={() => <>{children(undefined)}</>}
		>
			<Suspense fallback={null}>
				<HubBrandPrimaryColorQueryRenderer cloudId={cloudId} helpCenterAri={helpCenterAri}>
					{children}
				</HubBrandPrimaryColorQueryRenderer>
			</Suspense>
		</ErrorBoundary>
	);
};
